// Dayjs
import dayjs from 'lib/dayjs'

// Types
import { AssetData, Patient, Scan } from 'common/types'

// PDF
import { Page, Document, View, Text, Image, Link } from '@react-pdf/renderer'

// Utils
import { getAssetTypeLabel } from 'lib/utils/getAssetTypeLabel'

// Components
import { HeadCell } from 'components/molecules/DataTable/TableHeader'

// Types
import {
  AssetDetailsRowsData,
  SurgeryDetailsOutput,
} from '../PatientReport.types'
import { COLUMN_WIDTHS, HEAD_CELLS } from './constants'

// Styles
import { tableStyles, cardStyles, mainStyles } from './styles'
import { parseUdi } from 'lib/utils/codeIdentifierParser'
import { getReadableImplantStatus } from 'lib/utils/getReadableImplantStatus'

// Helper function to chunk array
const chunkArray = <T,>(array: T[], size: number): T[][] => {
  if (array.length === 0) return []
  const result: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size))
  }
  return result
}

const getColumnStyle = (columnId: string) => ({
  ...tableStyles.cell,
  ...(COLUMN_WIDTHS[columnId]
    ? {
        width: COLUMN_WIDTHS[columnId],
        marginRight: 8,
      }
    : { ...tableStyles.cellDefault, marginRight: 8 }),
})

const ProcedureDetailsReportPDF = ({
  surgeryDetails,
  isAdventist,
}: {
  surgeryDetails: SurgeryDetailsOutput
  isAdventist: boolean
}) => {
  const rootPath = window.location.origin
  const dateFormat = 'MMM DD, YYYY'

  const questions = surgeryDetails.questionnaire.questions
  const repsInfo = [
    ...new Set(
      surgeryDetails?.assetGroups
        ?.map((assetGroup) =>
          assetGroup.scans
            .filter(
              (scan) =>
                scan.implantStatus !== 'EXPLANTED' && scan.status.repInfo
            )
            .map((scan) => scan.status.repInfo)
        )
        .flat()
    ),
  ]

  const rowsOnFirstPage = repsInfo.length > 0 ? 4 : 5

  const procedureDate: SurgeryDetailsOutput['visitDate'] = dayjs(
    surgeryDetails?.visitDate
  ).format('hh:mm A , MMM DD, YYYY')
  const caseNumber: SurgeryDetailsOutput['surgicalCase']['number'] =
    surgeryDetails?.surgicalCase?.number
  const procedureType: SurgeryDetailsOutput['procedureType'] =
    surgeryDetails?.procedureType
  const surgeonLastName: SurgeryDetailsOutput['surgeon']['lastName'] =
    surgeryDetails?.surgeon?.lastName
  const surgeonFirstName: SurgeryDetailsOutput['surgeon']['firstName'] =
    surgeryDetails?.surgeon?.firstName

  const surgeonName = `${
    surgeonFirstName === undefined ? '' : surgeonFirstName
  } ${surgeonLastName === undefined ? '' : surgeonLastName}`

  const patientName = `${
    surgeryDetails?.patient?.lastName === undefined
      ? ''
      : surgeryDetails?.patient?.lastName
  }, ${
    surgeryDetails?.patient?.firstName === undefined
      ? ''
      : surgeryDetails?.patient?.firstName
  }`
  const patientDOB: Patient['dateOfBirth'] = dayjs(
    surgeryDetails?.patient?.dateOfBirth
  ).format(dateFormat)
  const patientMRN: Patient['idMR'] = surgeryDetails?.patient?.idMR

  const formatLongNumbers = (udi: string) => {
    if (!udi || udi === '-') return '-'

    // If UDI contains parentheses, format them with a new line
    let formattedUDI = udi.replace(/(\(\d+\))/g, '$1\n')

    // Break UDI into new lines every 15 characters
    return formattedUDI.replace(/(.{18})/g, '$1\n')
  }

  const getUDI = (udi: string) => {
    const { deviceId } = parseUdi(udi)
    return deviceId || udi
  }

  const assetTypeOrder = {
    biological: 1,
    'non-biological': 2,
    'other-non-biological': 3,
    consumable: 4,
    explanted: 5,
  } as const

  type AssetType = keyof typeof assetTypeOrder

  function compareAssetTypes(a: AssetDetailsRowsData, b: AssetDetailsRowsData) {
    const aType = (a.rawAssetType || '') as AssetType
    const bType = (b.rawAssetType || '') as AssetType

    return (
      (assetTypeOrder[aType] || Infinity) - (assetTypeOrder[bType] || Infinity)
    )
  }

  const formatValue = (value: number | undefined) => {
    if (value === undefined) {
      return '-'
    } else if (value < 1) {
      return value.toFixed(2)
    } else {
      return value.toString()
    }
  }
  const mappedData: AssetDetailsRowsData[] =
    surgeryDetails?.assetGroups?.flatMap((assetGroup: AssetData) =>
      assetGroup.scans.map((scan: Scan) => {
        const measurement = scan.measurement
        const unit = measurement?.unit || ''
        const quantity = unit
          ? formatValue(measurement?.value)
          : String(scan.count)

        return {
          serialNumber: formatLongNumbers(scan.serialNumber || '-'),
          catalogNumber: formatLongNumbers(scan.catalogNumber || '-'),
          udi: formatLongNumbers(getUDI(scan.udi || '-') || '-'),
          lotNumber: formatLongNumbers(scan.lotBatch || '-'),
          deviceDescription: scan.deviceDescription || '-',
          companyName: scan.companyName,
          quantity: quantity,
          assetType: getAssetTypeLabel(scan.assetType),
          implantSite: scan.implantSite || '-',
          disposition: scan.implantStatus
            ? getReadableImplantStatus(scan.implantStatus)
            : '-',
          rawAssetType: scan.assetType,
          measurement: measurement,
        }
      })
    )

  function groupData(products: AssetDetailsRowsData[]): AssetDetailsRowsData[] {
    const groupedMap = new Map<string, AssetDetailsRowsData>()

    products.forEach((item) => {
      const key = `${item.udi}-${item.lotNumber}-${item.serialNumber}-${item.disposition}-${item.implantSite}-${item.deviceDescription}`

      if (groupedMap.has(key)) {
        const existing = groupedMap.get(key)!
        if (!item.measurement) {
          existing.quantity += item.quantity
        }
      } else {
        groupedMap.set(key, { ...item })
      }
    })

    return Array.from(groupedMap.values()).sort(compareAssetTypes)
  }

  const chunkArrayWithDifferentSizes = (
    array: AssetDetailsRowsData[]
  ): AssetDetailsRowsData[][] => {
    return array.reduce(
      (resultArray: AssetDetailsRowsData[][], item, index) => {
        if (index < rowsOnFirstPage) {
          if (!resultArray[0]) resultArray[0] = []
          resultArray[0].push(item)
        } else {
          // For remaining items, create chunks of 10
          const chunkIndex = Math.floor((index - rowsOnFirstPage) / 10) + 1
          if (!resultArray[chunkIndex]) resultArray[chunkIndex] = []
          resultArray[chunkIndex].push(item)
        }
        return resultArray
      },
      []
    )
  }

  const TableChunk = ({
    headers,
    rows,
  }: {
    headers: HeadCell[]
    rows: AssetDetailsRowsData[]
  }) => (
    <View style={[tableStyles.container]}>
      <View style={tableStyles.headerRow}>
        {headers.map((header) => (
          <Text key={header.id} style={getColumnStyle(header.id as string)}>
            {header.label}
          </Text>
        ))}
      </View>

      {rows.map((row, index) => (
        <View
          key={index}
          style={index % 2 === 0 ? tableStyles.rowEven : tableStyles.rowOdd}
        >
          {headers.map((header) => {
            const cellValue = row[header.id as keyof typeof row] ?? '-'
            return (
              <Text key={header.id} style={getColumnStyle(header.id as string)}>
                {String(cellValue)}
              </Text>
            )
          })}
        </View>
      ))}
    </View>
  )

  const DetailRow = ({
    label,
    value,
  }: {
    label: string
    value: string | number
  }) => (
    <View style={cardStyles.row}>
      <Text style={cardStyles.label}>{label}:</Text>
      <Text
        style={
          String(value).length > 50
            ? String(value).length > 150
              ? cardStyles.valueLonger
              : cardStyles.valueLong
            : cardStyles.value
        }
      >
        {value}
      </Text>
    </View>
  )

  const Footer = () => (
    <View style={mainStyles.footer} fixed>
      <Link src="https://www.medgeo.com/">
        <View style={mainStyles.poweredByTextContainer}>
          <Text style={mainStyles.poweredByText}>Powered by</Text>
          <Image src={`${rootPath}/medgeoLogo.png`} style={[{ height: 18 }]} />
        </View>
      </Link>
    </View>
  )

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={mainStyles.page}>
        {isAdventist ? (
          <View style={mainStyles.header}>
            <Image
              src={`${rootPath}/adventistLogo.png`}
              style={[{ height: 50 }]}
            />
            <View style={mainStyles.addressBlock}>
              <Text style={mainStyles.addressText}>
                1500 E. Chevy Chase Drive, Suite #101
              </Text>
              <Text style={mainStyles.addressText}>Glendale, CA 91206</Text>
              <Text style={mainStyles.addressText}>(818) 863-4225</Text>
            </View>
          </View>
        ) : (
          <View style={mainStyles.emptyHeader} />
        )}

        <View style={mainStyles.headerContainer}>
          <Text style={mainStyles.headerTitle}>Procedure Implant Record</Text>
          {caseNumber ? (
            <View style={mainStyles.headerCaseWrapper}>
              <Text style={mainStyles.headerCaseLabel}>Case Number: </Text>
              <Text style={mainStyles.headerCaseValue}>#{caseNumber}</Text>
            </View>
          ) : null}
        </View>

        <View style={mainStyles.detailsGrid}>
          <View style={mainStyles.procedureDetailsSection}>
            <View
              style={
                (procedureType || '')?.length > 150
                  ? cardStyles.containerWithLongText
                  : cardStyles.container
              }
            >
              <Text style={cardStyles.title}>Procedure Details</Text>
              <DetailRow label="Surgeon" value={surgeonName} />
              <DetailRow label="Procedure Date" value={procedureDate} />
              <DetailRow label="Procedure Name" value={procedureType || '-'} />
            </View>
          </View>

          <View style={mainStyles.patientDetailsSection}>
            <View style={cardStyles.container}>
              <Text style={cardStyles.title}>Patient Details</Text>
              <DetailRow
                label="Patient MRN"
                value={`#${patientMRN || 'XXXXXX'}`}
              />
              <DetailRow label="Patient Name" value={patientName} />
              <DetailRow label="Date of Birth" value={patientDOB} />
            </View>
          </View>
        </View>

        {repsInfo.length > 0 && (
          <View style={mainStyles.repInfoSection}>
            <View style={cardStyles.repContainer}>
              <Text style={cardStyles.repLabel}>
                {`Rep${repsInfo.length > 1 ? 's' : ''} Information:`}
              </Text>
              <View style={cardStyles.repGrid}>
                {chunkArray(repsInfo, 3).map((rowReps, rowIndex) => (
                  <View key={rowIndex} style={cardStyles.repRow}>
                    {rowReps.map((rep, repIndex) => (
                      <View key={repIndex} style={cardStyles.repCell}>
                        <Text style={cardStyles.repValueItem}>{rep}</Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        <View style={mainStyles.questionnaireSection}>
          <View style={[cardStyles.questionnaireContainer]}>
            {questions?.length > 0 ? (
              questions?.map((item, index) => (
                <View key={index} style={cardStyles.questionRow}>
                  <Text style={cardStyles.question}>{item.question}</Text>
                  <Text style={cardStyles.answer}>
                    {item.answer === 'NA' ? 'N/A' : item.answer}
                  </Text>
                </View>
              ))
            ) : (
              <Text style={cardStyles.noQuestions}>No questions provided.</Text>
            )}
          </View>
        </View>

        <View>
          {chunkArrayWithDifferentSizes(groupData(mappedData))?.map(
            (chunk, index) => (
              <TableChunk
                key={index}
                headers={HEAD_CELLS}
                rows={chunk as AssetDetailsRowsData[]}
              />
            )
          )}
        </View>

        <Footer />
      </Page>
    </Document>
  )
}

export default ProcedureDetailsReportPDF
